/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { AccessTokenResponse } from '../models/access-token-response';
import { Uuid } from '../models/uuid';
import { UserResponse } from '../models/user-response';
import { UserRole } from '../models/user-role';

@Injectable({
  providedIn: 'root',
})
export class UsersService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getUsers
   */
  static readonly GetUsersPath = '/api/users';

  /**
   * Returns list of users ordered by family name
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<UserResponse>>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GetUsersPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<UserResponse>>;
      })
    );
  }

  /**
   * Returns list of users ordered by family name
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsers(params?: {
  },
  context?: HttpContext

): Observable<Array<UserResponse>> {

    return this.getUsers$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<UserResponse>>) => r.body as Array<UserResponse>)
    );
  }

  /**
   * Path part for operation createUser
   */
  static readonly CreateUserPath = '/api/users';

  /**
   * Create new user. 
   * User cannot already exist. 
   * User must be either Admin or Salesperson.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUser$Response(params: {

    /**
     * The email address of the user
     */
    email: string;

    /**
     * The user's givenName, which is also known as the first name
     */
    givenName: string;

    /**
     * The user's familyName, which is also known as the last name
     */
    familyName: string;

    /**
     * The unique identifier specific to this user, if it is undefined then it will be generated automatically
     */
    userId?: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UserResponse>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateUserPath, 'post');
    if (params) {
      rb.query('email', params.email, {});
      rb.query('givenName', params.givenName, {});
      rb.query('familyName', params.familyName, {});
      rb.query('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UserResponse>;
      })
    );
  }

  /**
   * Create new user. 
   * User cannot already exist. 
   * User must be either Admin or Salesperson.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUser(params: {

    /**
     * The email address of the user
     */
    email: string;

    /**
     * The user's givenName, which is also known as the first name
     */
    givenName: string;

    /**
     * The user's familyName, which is also known as the last name
     */
    familyName: string;

    /**
     * The unique identifier specific to this user, if it is undefined then it will be generated automatically
     */
    userId?: Uuid;
  },
  context?: HttpContext

): Observable<UserResponse> {

    return this.createUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<UserResponse>) => r.body as UserResponse)
    );
  }

  /**
   * Path part for operation getUser
   */
  static readonly GetUserPath = '/api/users/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUser$Response(params: {

    /**
     * user id
     */
    id: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UserResponse>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GetUserPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UserResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUser(params: {

    /**
     * user id
     */
    id: Uuid;
  },
  context?: HttpContext

): Observable<UserResponse> {

    return this.getUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<UserResponse>) => r.body as UserResponse)
    );
  }

  /**
   * Path part for operation updateUser
   */
  static readonly UpdateUserPath = '/api/users/{id}';

  /**
   * Update existing user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateUser$Response(params: {

    /**
     * Unique identifier for this user
     */
    id: Uuid;

    /**
     * The user's email address
     */
    email?: string;

    /**
     * The user's given name (aka first name)
     */
    givenName?: string;

    /**
     * The user's family name (aka last name)
     */
    familyName?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UserResponse>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.UpdateUserPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('email', params.email, {});
      rb.query('givenName', params.givenName, {});
      rb.query('familyName', params.familyName, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UserResponse>;
      })
    );
  }

  /**
   * Update existing user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateUser(params: {

    /**
     * Unique identifier for this user
     */
    id: Uuid;

    /**
     * The user's email address
     */
    email?: string;

    /**
     * The user's given name (aka first name)
     */
    givenName?: string;

    /**
     * The user's family name (aka last name)
     */
    familyName?: string;
  },
  context?: HttpContext

): Observable<UserResponse> {

    return this.updateUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<UserResponse>) => r.body as UserResponse)
    );
  }

  /**
   * Path part for operation deleteUser
   */
  static readonly DeleteUserPath = '/api/users/{id}';

  /**
   * Delete user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUser$Response(params: {

    /**
     * user id
     */
    id: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteUserPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUser(params: {

    /**
     * user id
     */
    id: Uuid;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteUser$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getMe
   */
  static readonly GetMePath = '/api/users/me';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMe()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMe$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<UserResponse>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GetMePath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<UserResponse>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMe$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMe(params?: {
  },
  context?: HttpContext

): Observable<UserResponse> {

    return this.getMe$Response(params,context).pipe(
      map((r: StrictHttpResponse<UserResponse>) => r.body as UserResponse)
    );
  }

  /**
   * Path part for operation getAccessToken
   */
  static readonly GetAccessTokenPath = '/api/users/token';

  /**
   * Obtains a fresh token for authenticated user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAccessToken()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAccessToken$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<AccessTokenResponse>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GetAccessTokenPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<AccessTokenResponse>;
      })
    );
  }

  /**
   * Obtains a fresh token for authenticated user.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAccessToken$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAccessToken(params?: {
  },
  context?: HttpContext

): Observable<AccessTokenResponse> {

    return this.getAccessToken$Response(params,context).pipe(
      map((r: StrictHttpResponse<AccessTokenResponse>) => r.body as AccessTokenResponse)
    );
  }

  /**
   * Path part for operation updateAvatarUrl
   */
  static readonly UpdateAvatarUrlPath = '/api/users/{userId}/avatar';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAvatarUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateAvatarUrl$Response(params: {
    userId: Uuid;
    avatarUrl: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.UpdateAvatarUrlPath, 'put');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('avatarUrl', params.avatarUrl, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAvatarUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateAvatarUrl(params: {
    userId: Uuid;
    avatarUrl: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.updateAvatarUrl$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createAvatarUrl
   */
  static readonly CreateAvatarUrlPath = '/api/users/{userId}/avatar';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAvatarUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  createAvatarUrl$Response(params: {
    userId: Uuid;
    avatarUrl: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateAvatarUrlPath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('avatarUrl', params.avatarUrl, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAvatarUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createAvatarUrl(params: {
    userId: Uuid;
    avatarUrl: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.createAvatarUrl$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteAvatarUrl
   */
  static readonly DeleteAvatarUrlPath = '/api/users/{userId}/avatar';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAvatarUrl()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAvatarUrl$Response(params: {
    userId: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteAvatarUrlPath, 'delete');
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAvatarUrl$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAvatarUrl(params: {
    userId: Uuid;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteAvatarUrl$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateUserExtraDetails
   */
  static readonly UpdateUserExtraDetailsPath = '/api/users/{userId}/details';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUserExtraDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateUserExtraDetails$Response(params: {
    userId: Uuid;
    companyName?: string;
    industry?: string;
    comments?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.UpdateUserExtraDetailsPath, 'put');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('companyName', params.companyName, {});
      rb.query('industry', params.industry, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUserExtraDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateUserExtraDetails(params: {
    userId: Uuid;
    companyName?: string;
    industry?: string;
    comments?: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.updateUserExtraDetails$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createUserExtraDetails
   */
  static readonly CreateUserExtraDetailsPath = '/api/users/{userId}/details';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUserExtraDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUserExtraDetails$Response(params: {
    userId: Uuid;
    companyName: string;
    industry: string;
    comments: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateUserExtraDetailsPath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('companyName', params.companyName, {});
      rb.query('industry', params.industry, {});
      rb.query('comments', params.comments, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUserExtraDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUserExtraDetails(params: {
    userId: Uuid;
    companyName: string;
    industry: string;
    comments: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.createUserExtraDetails$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteUserExtraDetails
   */
  static readonly DeleteUserExtraDetailsPath = '/api/users/{userId}/details';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUserExtraDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserExtraDetails$Response(params: {
    userId: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteUserExtraDetailsPath, 'delete');
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUserExtraDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserExtraDetails(params: {
    userId: Uuid;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteUserExtraDetails$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createUserRole
   */
  static readonly CreateUserRolePath = '/api/users/{userId}/role';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUserRole()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUserRole$Response(params: {
    userId: Uuid;
    role: UserRole;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateUserRolePath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('role', params.role, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUserRole$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createUserRole(params: {
    userId: Uuid;
    role: UserRole;
  },
  context?: HttpContext

): Observable<void> {

    return this.createUserRole$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteUserRole
   */
  static readonly DeleteUserRolePath = '/api/users/{userId}/role';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteUserRole()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserRole$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Role being removed from user
     */
    role: UserRole;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteUserRolePath, 'delete');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('role', params.role, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteUserRole$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteUserRole(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Role being removed from user
     */
    role: UserRole;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteUserRole$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateClientId
   */
  static readonly UpdateClientIdPath = '/api/users/{userId}/clientId';

  /**
   * Update client ID for a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientId()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateClientId$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * The new client ID value
     */
    clientId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.UpdateClientIdPath, 'put');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('clientId', params.clientId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Update client ID for a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClientId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateClientId(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * The new client ID value
     */
    clientId: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.updateClientId$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createClientId
   */
  static readonly CreateClientIdPath = '/api/users/{userId}/clientId';

  /**
   * Create client ID for a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientId()` instead.
   *
   * This method doesn't expect any request body.
   */
  createClientId$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * The client ID to assign to the user
     */
    clientId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateClientIdPath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('clientId', params.clientId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Create client ID for a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClientId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  createClientId(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * The client ID to assign to the user
     */
    clientId: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.createClientId$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteClientId
   */
  static readonly DeleteClientIdPath = '/api/users/{userId}/clientId';

  /**
   * Delete client ID for a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteClientId()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientId$Response(params: {

    /**
     * User id
     */
    userId: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteClientIdPath, 'delete');
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete client ID for a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteClientId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteClientId(params: {

    /**
     * User id
     */
    userId: Uuid;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteClientId$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getCertificates
   */
  static readonly GetCertificatesPath = '/api/users/{userId}/certificates';

  /**
   * List all certificates registered for a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCertificates()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCertificates$Response(params: {

    /**
     * User id
     */
    userId: Uuid;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<Array<{
'expiresAt': string;
'label': string | null;
'thumbprint': string;
}>>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GetCertificatesPath, 'get');
    if (params) {
      rb.path('userId', params.userId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<{
        'expiresAt': string;
        'label': string | null;
        'thumbprint': string;
        }>>;
      })
    );
  }

  /**
   * List all certificates registered for a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCertificates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCertificates(params: {

    /**
     * User id
     */
    userId: Uuid;
  },
  context?: HttpContext

): Observable<Array<{
'expiresAt': string;
'label': string | null;
'thumbprint': string;
}>> {

    return this.getCertificates$Response(params,context).pipe(
      map((r: StrictHttpResponse<Array<{
'expiresAt': string;
'label': string | null;
'thumbprint': string;
}>>) => r.body as Array<{
'expiresAt': string;
'label': string | null;
'thumbprint': string;
}>)
    );
  }

  /**
   * Path part for operation createCertificate
   */
  static readonly CreateCertificatePath = '/api/users/{userId}/certificates';

  /**
   * Register an externally-generated certificate for a user by uploading the .crt file.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCertificate()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createCertificate$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Human-readable label for this certificate (e.g. "my-daemon")
     */
    label: string;
    body: {

/**
 * PEM certificate file (.crt)
 */
'cert': Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<{
'expiresAt': string;
'thumbprint': string;
}>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.CreateCertificatePath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('label', params.label, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'expiresAt': string;
        'thumbprint': string;
        }>;
      })
    );
  }

  /**
   * Register an externally-generated certificate for a user by uploading the .crt file.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCertificate$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createCertificate(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Human-readable label for this certificate (e.g. "my-daemon")
     */
    label: string;
    body: {

/**
 * PEM certificate file (.crt)
 */
'cert': Blob;
}
  },
  context?: HttpContext

): Observable<{
'expiresAt': string;
'thumbprint': string;
}> {

    return this.createCertificate$Response(params,context).pipe(
      map((r: StrictHttpResponse<{
'expiresAt': string;
'thumbprint': string;
}>) => r.body as {
'expiresAt': string;
'thumbprint': string;
})
    );
  }

  /**
   * Path part for operation generateCertificate
   */
  static readonly GenerateCertificatePath = '/api/users/{userId}/certificates/generate';

  /**
   * Generate a CA-signed certificate for a user and return it as a zip download.
   * The thumbprint is automatically registered. The label identifies the daemon
   * and is used as a prefix for all files in the zip.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateCertificate()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateCertificate$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Human-readable label for this certificate (e.g. "collab-daemon")
     */
    label: string;

    /**
     * Validity period in days (default: 365)
     */
    days?: number;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.GenerateCertificatePath, 'post');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.query('label', params.label, {});
      rb.query('days', params.days, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Generate a CA-signed certificate for a user and return it as a zip download.
   * The thumbprint is automatically registered. The label identifies the daemon
   * and is used as a prefix for all files in the zip.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateCertificate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateCertificate(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * Human-readable label for this certificate (e.g. "collab-daemon")
     */
    label: string;

    /**
     * Validity period in days (default: 365)
     */
    days?: number;
  },
  context?: HttpContext

): Observable<void> {

    return this.generateCertificate$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteCertificate
   */
  static readonly DeleteCertificatePath = '/api/users/{userId}/certificates/{thumbprint}';

  /**
   * Remove a certificate from a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCertificate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCertificate$Response(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * SHA-1 thumbprint of the certificate to remove (lowercase hex, no colons)
     */
    thumbprint: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, UsersService.DeleteCertificatePath, 'delete');
    if (params) {
      rb.path('userId', params.userId, {});
      rb.path('thumbprint', params.thumbprint, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Remove a certificate from a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteCertificate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCertificate(params: {

    /**
     * User id
     */
    userId: Uuid;

    /**
     * SHA-1 thumbprint of the certificate to remove (lowercase hex, no colons)
     */
    thumbprint: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.deleteCertificate$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
